import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { WorldPayTransaction, verifyWorldPayTransaction } from '../../../API/GoMoto';
import { useFullReset, useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useAppointmentStateUnsafe } from '../../../context/Appointment/appointment';
import { PaymentStatus } from './PaymentStatus';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { OnlinePaymentRoutePaths } from '../../../helpers/Routes';
import { GMDialogError } from '../../../controls/Dialog';

interface IProps {
    onGetKeys: () => void;
    onReturn: (route: OnlinePaymentRoutePaths, errorMessage?: string) => void;
}

export const WorldPayPaymentStatus: React.FC<IProps> = ({ onGetKeys, onReturn }) => {
    const appointment = useAppointmentStateUnsafe();
    const hasEarlierPayment: boolean = !!appointment?.paymentTransactionId;
    const [transaction, setTransaction] = useState<WorldPayTransaction | undefined | null>(undefined);
    const location = useLocation();
    const {
        dealer: { key: dealerKey },
    } = useKioskState();
    const reset = useFullReset(true, false);
    const Localization = useLocalization();
    const noPaymentDue: boolean = appointment?.paymentStatus === 'no payment due' || appointment?.paymentStatus === 'marked paid';
    const [fetchError, setFetchError] = useState<boolean>(false);

    useEffect(() => {
        if (!appointment) {
            reset();
        }
    }, [appointment, reset]);

    useEffect(() => {
        if (!appointment || noPaymentDue || hasEarlierPayment) {
            setTransaction(null);
        }
        else if (transaction === undefined) {
            const urlSearchParams = new URLSearchParams(window.location.search);

            const expressResponseCode: string | null = urlSearchParams.get('ExpressResponseCode') ?? '';
            const cvvResponseCode: string = urlSearchParams.get('CVVResponseCode') ?? '';
            const avsResponseCode: string | null = urlSearchParams.get('AVSResponseCode') ?? '';
            const transactionId: string | null = urlSearchParams.get('TransactionID') ?? '';
            const validationCode: string | null = urlSearchParams.get('ValidationCode') ?? '';

            verifyWorldPayTransaction(transactionId, validationCode, expressResponseCode, cvvResponseCode, avsResponseCode, dealerKey, appointment.key)
                .then((transaction: WorldPayTransaction | null) => {
                    if (transaction === null || transaction.verificationResult === null) {
                        onReturn(OnlinePaymentRoutePaths.Landing);
                    }
                    else if (!transaction.verificationResult.sucessfulTransaction) {
                        let errorMessage: string = '';

                        if (['7', '8', '20', '21', '24', '25', '104', '105', '120'].includes(transaction.verificationResult.expressResponseCode)) {
                            errorMessage = Localization.OhNoYourPaymentFailed;
                        }
                        else if (
                            !transaction.verificationResult.expressResponseCode
                            || ['30', '90', '1001', '1002', '1009'].includes(transaction.verificationResult.expressResponseCode)
                        ) {
                            errorMessage = Localization.AnErrorHasOccurredSelectPayToTryAgain;
                        }
                        else if (
                            ['101', '102', '103'].includes(transaction.verificationResult.expressResponseCode) ||
                            (
                                transaction.verificationResult.successfulExpressResponseCode
                                && (!transaction.verificationResult.successfulAVSResponseCode || !transaction.verificationResult.successfulCVVResponseCode)
                            )
                        ) {
                            errorMessage = Localization.SomethingIsntRightCheckYourPaymentDetails;
                        }

                        onReturn(OnlinePaymentRoutePaths.Payment, errorMessage);
                    }
                    else {
                        setTransaction(transaction);
                    }
                })
                .catch(() => setFetchError(true));
        }
    }, [
        appointment,
        transaction,
        dealerKey,
        location,
        onReturn,
        noPaymentDue,
        hasEarlierPayment,
        Localization.OhNoYourPaymentFailed,
        Localization.AnErrorHasOccurredSelectPayToTryAgain,
        Localization.SomethingIsntRightCheckYourPaymentDetails,
    ]);

    return (
        <>
            {!appointment || transaction === undefined ?
                <Spinner /> :
                <PaymentStatus
                    paymentDetails={transaction?.paymentDetails}
                    paymentComplete
                    onGetKeys={onGetKeys}
                    paymentIdLabel={Localization.TransactionId}
                />
            }
            <GMDialogError
                ariaLabel={Localization.FailedToGetPaymentStatus}
                open={fetchError}
                onClose={reset}
            >
                {Localization.FailedToGetPaymentStatus}
            </GMDialogError>
        </>
    );
};
